// components/SummaryViewPage.js
// Dedicated, standalone page for the Summary View feature — reachable from
// its own card on both clinician-facing surfaces (normal-flow Clinician.js
// and staff TelehealthReviewDashboard.js) rather than being embedded inline
// on top of another page. variant="clinician" resolves its auth token from
// UserContext (same as the rest of the normal clinician flow);
// variant="telehealth" resolves it from the "telehealthReviewer" localStorage
// entry (same as TelehealthReviewDashboard itself).
import React, { useContext, useEffect } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import SummaryView from "./SummaryView";

const BRAND = { navy: "#0a0e27", accent: "#637cdb" };

export default function SummaryViewPage({ variant }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const staff = variant === "telehealth"
    ? JSON.parse(localStorage.getItem("telehealthReviewer") || "null")
    : null;

  const token = variant === "telehealth" ? staff?.token : user?.token;
  const backTo = variant === "telehealth" ? "/telehealth-review-dashboard" : "/clinician-bot";
  const displayName = variant === "telehealth" ? (staff?.name || staff?.email) : user?.name;

  // The "/telehealth-summary-view" route isn't wrapped in <ProtectedRoute>
  // (that component only knows about the normal UserContext-based auth) —
  // this mirrors TelehealthReviewDashboard's own guard for its separate
  // localStorage-based staff auth.
  useEffect(() => {
    if (variant === "telehealth" && !staff?.token) {
      navigate("/telehealth-review-login", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const bg = isDark ? "#0a0f1e" : "#f0f4ff";
  const cardBg = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.72)";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: bg, position: "relative", overflow: "hidden" }}>
      {/* Animated background blobs — same treatment as the rest of the app's glass pages */}
      <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
        {[
          { top: "-14%", left: "-8%", size: 500, color: isDark ? "rgba(10,14,39,0.2)" : "rgba(10,14,39,0.06)", delay: 0 },
          { top: "55%", right: "-12%", size: 440, color: isDark ? "rgba(99,124,219,0.08)" : "rgba(99,124,219,0.1)", delay: 1.4 },
        ].map((b, i) => (
          <motion.div key={i} style={{
            position: "absolute", top: b.top, left: b.left, right: b.right,
            width: b.size, height: b.size, borderRadius: "50%",
            background: b.color, filter: "blur(96px)",
          }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 10 + i * 2, repeat: Infinity, delay: b.delay, ease: "easeInOut" }}
          />
        ))}
      </Box>

      <Box sx={{ position: "relative", zIndex: 10, maxWidth: 1100, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
        <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45, ease: "easeOut" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Button
                onClick={() => navigate(backTo)}
                startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none", fontSize: 12.5, fontWeight: 600, mb: 1.5, px: 0,
                  color: isDark ? "#94a3b8" : "#64748b",
                  "&:hover": { bgcolor: "transparent", color: BRAND.accent },
                }}
              >
                Back
              </Button>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", color: BRAND.accent, mb: 0.75 }}>
                CLINICIAN TOOLS
              </Typography>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, lineHeight: 1.2 }}>
                Summary View
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: isDark ? "#94a3b8" : "#64748b", mt: 0.75 }}>
                {displayName ? `Signed in as ${displayName} — ` : ""}every report type, one visual snapshot, per patient.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ThemeToggle />
            </Stack>
          </Box>
        </motion.div>

        <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}>
          <Box sx={{
            borderRadius: "22px", overflow: "hidden", p: { xs: 2, sm: 3 },
            bgcolor: cardBg, backdropFilter: "blur(20px) saturate(140%)",
            WebkitBackdropFilter: "blur(20px) saturate(140%)",
            border: `1px solid ${border}`,
            boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
          }}>
            <SummaryView variant={variant} token={token} />
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
